import { ApiResult, HttpClient } from "../http.js";
import {
  LeaveEntryDto,
  LeaveHalfDayPortion,
  LeaveType,
  PagedLeaveEntries,
} from "../types/leave.js";

export interface CreateLeaveEntryPayload {
  userId: string;
  userName: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  isHalfDay?: boolean;
  halfDayPortion?: LeaveHalfDayPortion | null;
  title?: string | null;
  notes?: string | null;
  id?: string;
}

export interface ListLeaveEntriesQuery {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateLeaveEntryPayload {
  type: LeaveType;
  startDate: string;
  endDate: string;
  isHalfDay?: boolean;
  halfDayPortion?: LeaveHalfDayPortion | null;
  title?: string | null;
  notes?: string | null;
}

export interface RejectLeaveEntryPayload {
  reason?: string | null;
}

export class LeaveApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListLeaveEntriesQuery = {}): Promise<ApiResult<PagedLeaveEntries>> {
    return this.http.get<PagedLeaveEntries>("/api/leave-entries", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<LeaveEntryDto>> {
    return this.http.get<LeaveEntryDto>(`/api/leave-entries/${encodeURIComponent(id)}`);
  }

  create(payload: CreateLeaveEntryPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/leave-entries", { body: payload });
  }

  update(id: string, payload: UpdateLeaveEntryPayload): Promise<ApiResult<LeaveEntryDto>> {
    return this.http.put<LeaveEntryDto>(`/api/leave-entries/${encodeURIComponent(id)}`, { body: payload });
  }

  delete(id: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/leave-entries/${encodeURIComponent(id)}`);
  }

  // ── Approval workflow ──────────────────────────────────────────────────────
  // Leave's approval is a single status flip (no submit/revise step). Mirrors the
  // Firestore `useUpdateLeaveStatus` write shape 1:1.

  /** Approve a leave entry (→ Approved). */
  approve(id: string): Promise<ApiResult<LeaveEntryDto>> {
    return this.http.post<LeaveEntryDto>(`/api/leave-entries/${encodeURIComponent(id)}/approve`, {});
  }

  /** Reject a leave entry with an optional reason (→ Rejected). */
  reject(id: string, payload: RejectLeaveEntryPayload = {}): Promise<ApiResult<LeaveEntryDto>> {
    return this.http.post<LeaveEntryDto>(`/api/leave-entries/${encodeURIComponent(id)}/reject`, { body: payload });
  }

  /** Cancel a leave entry (→ Cancelled). */
  cancel(id: string): Promise<ApiResult<LeaveEntryDto>> {
    return this.http.post<LeaveEntryDto>(`/api/leave-entries/${encodeURIComponent(id)}/cancel`, {});
  }

  /** Entries awaiting review (Pending) the caller is allowed to approve. */
  pending(): Promise<ApiResult<LeaveEntryDto[]>> {
    return this.http.get<LeaveEntryDto[]>("/api/leave-entries/pending");
  }

  /** The caller's own leave entries (all statuses). */
  mySubmissions(): Promise<ApiResult<LeaveEntryDto[]>> {
    return this.http.get<LeaveEntryDto[]>("/api/leave-entries/my-submissions");
  }
}
